/**
 * Labstep
 *
 * @module components/FilterPost/NewIndex/Metadata/Active/Post
 * @desc Active node component for metadata
 */

import type { FilterPostActiveLabelProps } from 'labstep-web/components/FilterPost/Active/Label/types';
import { FilterPostNewIndexMetadata } from 'labstep-web/components/FilterPost/NewIndex/Metadata';
import { ICONS } from 'labstep-web/constants/icons';
import { useParamsContext } from 'labstep-web/contexts/params/hook';
import { RemoveLabel } from 'labstep-web/core/Label/Remove';
import {
  MetadataType,
  MetadataTypeValues,
} from 'labstep-web/models/metadata/types';
import type { PostFilterComparisonType } from 'labstep-web/services/postFilter/constants';
import { PostFilterComparison } from 'labstep-web/services/postFilter/constants';
import {
  getPostFilterMetadatasDate,
  getPostFilterMetadatasDatetime,
  getPostFilterMetadatasDefault,
  getPostFilterMetadatasNumeric,
  getPostFilterMetadatasOptions,
  getPostFilterMetadatasSequence,
} from 'labstep-web/services/postFilter/filters/metadatas';
import type React from 'react';
import { getComparisonLabelDict } from '../../Form/fields';
import type { FilterMetadataType } from '../../Form/types';

const PATHS = [
  'metadatas',
  'purchaseOrder.metadatas',
  'resource.metadatas',
];

const FILTERS = PATHS.flatMap((path) => [
  getPostFilterMetadatasDate(path),
  getPostFilterMetadatasDatetime(path),
  getPostFilterMetadatasDefault(path),
  getPostFilterMetadatasNumeric(path),
  getPostFilterMetadatasOptions(path),
]);

export const FilterPostNewIndexMetadataActivePost: React.FC<
  FilterPostActiveLabelProps
> = ({ node, onRemoveClick, index }) => {
  const { addPostFilter } = useParamsContext();
  const filterActive = FILTERS.find((filter) =>
    filter.isNodeActive(node),
  );
  if (!filterActive) {
    return null;
  }
  const attributes = filterActive.getAttributeValues(node);

  if (
    attributes?.value?.comparison === PostFilterComparison.not_null
  ) {
    return null;
  }

  let type: FilterMetadataType;
  let comparison: PostFilterComparisonType;
  let value: string;
  if (attributes.options) {
    type = MetadataType.options;
    comparison = attributes.options.comparison;
    value = attributes.options.value;
  } else if (attributes.date) {
    type = MetadataType.date;
    comparison = attributes.date.comparison;
    value = attributes.date.value;
  } else if (attributes.number) {
    type = MetadataType.numeric;
    comparison = attributes.number.comparison;
    value = attributes.number.value;
  } else if (attributes.inchis) {
    type = MetadataType.sequence;
    comparison = attributes.inchis.comparison;
    value = attributes.inchis.value;
  } else {
    type = MetadataType.default;
    comparison = attributes.value.comparison;
    value = attributes.value.value;
  }

  const defaultValues = {
    label: attributes.label?.value,
    type: {
      value: type,
      label:
        MetadataTypeValues[type as keyof typeof MetadataTypeValues],
    },
    comparison: {
      value: comparison,
      label: getComparisonLabelDict(type)[comparison],
    },
    value,
  };

  return (
    <FilterPostNewIndexMetadata
      path={filterActive.getPath() || ''}
      addPostFilter={addPostFilter}
      defaultValues={defaultValues}
      viewComponent={({ toggleModal }) => (
        <RemoveLabel
          name={filterActive.getHumanReadableString(
            node,
            defaultValues.comparison.label,
          )}
          icon={
            filterActive.getPath()?.includes('resource')
              ? ICONS.metadata.secondary
              : ICONS.metadata.primary
          }
          onNameClick={toggleModal}
          onRemoveClick={onRemoveClick}
        />
      )}
      replace={index}
    />
  );
};
